export type CanvasIdentifier = string | number;

export interface CanvasNavigationBreadcrumb {
  canvasId: CanvasIdentifier;
}

export interface InlineCanvasBackPlan<
  TBreadcrumb extends CanvasNavigationBreadcrumb,
> {
  saveCanvasId: CanvasIdentifier;
  saveUrl: string;
  targetBreadcrumb: TBreadcrumb;
  remainingBreadcrumbs: TBreadcrumb[];
}

export function planInlineCanvasEntry<TSnapshot>({
  activeCanvasId,
  childCanvasId,
  breadcrumbs,
  currentSnapshot,
}: {
  activeCanvasId: CanvasIdentifier;
  childCanvasId: CanvasIdentifier;
  breadcrumbs: Array<TSnapshot & CanvasNavigationBreadcrumb>;
  currentSnapshot: TSnapshot;
}): {
  activeCanvasId: CanvasIdentifier;
  breadcrumbs: Array<TSnapshot & CanvasNavigationBreadcrumb>;
} {
  return {
    activeCanvasId: childCanvasId,
    breadcrumbs: [
      ...breadcrumbs,
      { ...currentSnapshot, canvasId: activeCanvasId },
    ],
  };
}

/**
 * Resolve the save target and restored state when leaving an inline child.
 * This seam initially preserves the route/breadcrumb-derived target so the
 * wrong-canvas request can be captured by a runnable red test.
 */
export function planInlineCanvasBackNavigation<
  TBreadcrumb extends CanvasNavigationBreadcrumb,
>({
  routeCanvasId: _routeCanvasId,
  activeCanvasId,
  breadcrumbs,
  targetIndex,
}: {
  routeCanvasId: CanvasIdentifier;
  activeCanvasId: CanvasIdentifier;
  breadcrumbs: TBreadcrumb[];
  targetIndex: number;
}): InlineCanvasBackPlan<TBreadcrumb> {
  const targetBreadcrumb = breadcrumbs[targetIndex];
  if (!targetBreadcrumb) {
    throw new Error("Invalid inline canvas breadcrumb target");
  }

  const saveCanvasId = activeCanvasId;

  return {
    saveCanvasId,
    saveUrl: `/api/workspace/myvault/canvases/${saveCanvasId}`,
    targetBreadcrumb,
    remainingBreadcrumbs: breadcrumbs.slice(0, targetIndex),
  };
}
